import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import { isNative } from '../native/platform';
import {
  clearRefreshToken,
  loadRefreshToken,
  persistRefreshToken,
} from '../native/secure-session';
import {
  driverAuthConfig,
  getAccessToken,
  handleCallback,
  login,
  logout,
  refreshAccessToken,
  setAccessToken,
  TokenRefreshError,
} from './driver-auth';

interface DriverAuthContextValue {
  /** current access token, or null when unauthenticated */
  token: string | null;
  /** true while a native cold-launch biometric-resume is in flight (web: always false) */
  resuming: boolean;
  /** kick off the Keycloak Authorization-Code + PKCE redirect */
  login: () => Promise<void>;
  /** complete the `/callback` round-trip; resolves true on success */
  handleCallback: () => Promise<boolean>;
  /** clear the in-memory + sessionStorage token (and the Keystore refresh token) */
  logout: () => void;
}

const DriverAuthContext = createContext<DriverAuthContextValue | null>(null);

export function DriverAuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() => getAccessToken());
  const onCallback =
    typeof window !== 'undefined' && window.location.pathname.endsWith('/callback');
  // Only a native cold-launch with no live access token attempts a biometric resume.
  // On web this is false, so the boot effect below never runs and behavior is unchanged.
  const [resuming, setResuming] = useState<boolean>(
    () => isNative() && !getAccessToken() && !onCallback
  );

  // Biometric-resume boot: native cold-launch → Keystore refresh token → refresh-grant a fresh
  // access token (no full re-login). On no-token/deny → fall through to login(); on a GENUINELY DEAD
  // RT (auth failure) → discard it; a transient/offline failure PRESERVES it (see the catch).
  // Skipped on /callback and on web (resuming starts false there).
  useEffect(() => {
    if (!resuming) return;
    let cancelled = false;
    void (async () => {
      try {
        const rt = await loadRefreshToken(); // biometric-gated; null if none/denied
        if (rt) {
          const cfg = driverAuthConfig();
          const { access_token, refresh_token } = await refreshAccessToken(cfg, rt);
          setAccessToken(access_token);
          if (refresh_token) await persistRefreshToken(refresh_token);
          if (!cancelled) setToken(access_token);
        }
      } catch (err) {
        // Discard the Keystore refresh token ONLY when it is GENUINELY dead (an auth failure —
        // Keycloak 4xx invalid_grant). A transient/offline/5xx failure must PRESERVE it so a later
        // launch can retry biometric-resume; otherwise an OFFLINE cold-launch (the field driver's
        // primary scenario) would silently destroy a valid token and force a network-dependent re-login.
        if (err instanceof TokenRefreshError && err.authFailure) {
          await clearRefreshToken();
        }
      } finally {
        if (!cancelled) setResuming(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [resuming]);

  // Auth-ready signal: when an access token lands (login-callback OR biometric-resume), tell the
  // outbox to flush any deferred ops (OFF-3 — the outbox subscribes to this; no provider coupling).
  useEffect(() => {
    if (token) window.dispatchEvent(new Event('pipe:driver:auth-ready'));
  }, [token]);

  const doLogin = useCallback(() => login(driverAuthConfig()), []);

  const doHandleCallback = useCallback(async () => {
    const { ok, refreshToken } = await handleCallback(driverAuthConfig());
    if (ok && refreshToken && isNative()) {
      await persistRefreshToken(refreshToken);
    }
    setToken(getAccessToken());
    return ok;
  }, []);

  const doLogout = useCallback(() => {
    logout();
    void clearRefreshToken();
    setToken(null);
  }, []);

  return (
    <DriverAuthContext.Provider
      value={{
        token,
        resuming,
        login: doLogin,
        handleCallback: doHandleCallback,
        logout: doLogout,
      }}
    >
      {children}
    </DriverAuthContext.Provider>
  );
}

export function useDriverAuth(): DriverAuthContextValue {
  const ctx = useContext(DriverAuthContext);
  if (!ctx) {
    throw new Error('useDriverAuth must be used within a DriverAuthProvider');
  }
  return ctx;
}
